import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Select, MenuItem, Tooltip, IconButton, Icon } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    }
}));

const Departments = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        departments: [],
        department: {
            iddepartment: 0,
            name: '',
            use_ticket: false,
            hourly_rate: 0,
            production_pack: false
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Departments';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/departments');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, departments: response?.data ?? [] }));
        } else {
            console.log('[Departments] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, department: { ...prev.department, [target.name]: target.value } }));

    const validateForm = (department) => {
        const { iddepartment, name, hourly_rate } = state.department;

        switch (department) {
            case 'update':
                const data = state.departments.filter(value => value.iddepartment === iddepartment);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.department);
                    if (before !== after) {
                        if (name.trim() && hourly_rate) {
                            const cond = window.confirm('If you update the hourly rate field, then all affected data will be updated: \n- Labors \n- Recipe labors \n\nDo you want to continue with this update?');

                            return cond;
                        } else {
                            showToast('All fields is required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select some department', 'error');
                    return false;
                }

            case 'store':

                if (name.trim() && hourly_rate) {
                    return true;
                } else {
                    showToast('All fields is required', 'error');
                    return false;
                }

            case 'delete':
                if (iddepartment === 0) {
                    showToast('Select some department', 'error');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this department?');

                return Boolean(iddepartment && cond);
            default:
                break;
        }
    }

    const clear = () => {
        setState(prev => ({ ...prev, department: { ...prev.department, iddepartment: 0, name: '', use_ticket: false, hourly_rate: 0, production_pack: false } }));
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/department/store', state.department);
            if (response.status === 200) {
                setState(prev => ({ ...prev, departments: response?.data ?? prev.departments }));
                showToast(response.message);
            } else {
                console.log('[Departments] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { iddepartment } = state.department;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/department/${iddepartment}/update`, state.department);
            if (response.status === 200) {
                setState(prev => ({ ...prev, departments: response?.data ?? prev.departments }));
                showToast(response.message);
            } else {
                console.log('[Departments] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { iddepartment } = state.department;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/department/${iddepartment}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, departments: response?.data ?? prev.departments }));
                showToast(response.message);
            } else {
                console.log('[Departments] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const valueGetter = ({ row, field }) => row[field] ? '✓' : '✗';

    const getHR = ({ row, field }) => row?.sedes[0]?.[field] ?? 'N/A';

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/departments/${row.iddepartment}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 130 },
        { field: 'use_ticket', headerName: 'Use ticket', flex: 1, minWidth: 100, valueGetter },
        { field: 'hourly_rate', headerName: 'Hourly rate', flex: 1, minWidth: 100, valueGetter: getHR },
        { field: 'production_pack', headerName: 'Production pack', flex: 1, minWidth: 100, valueGetter },
        { field: 'actions', headerName: 'Actions', flex: 0.5, minWidth: 100, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }
    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <ViewTitle title='Departments' />
            <div>
                <Grid container>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.department.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormGroup>
                            <FormLabel>{'Use ticket'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    value={state.department.use_ticket}
                                    name='use_ticket'
                                    onChange={handleChange}
                                >
                                    <MenuItem value={false}>{'No'}</MenuItem>
                                    <MenuItem value={true}>{'Yes'}</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormGroup>
                            <FormLabel>{'Hourly rate'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='number'
                                    placeholder='Hourly rate'
                                    name='hourly_rate'
                                    value={state.department.hourly_rate}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={4} xl={3}>
                        <FormGroup>
                            <FormLabel>{'Production pack'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <Select
                                    value={state.department.production_pack}
                                    name='production_pack'
                                    onChange={handleChange}
                                >
                                    <MenuItem value={false}>{'No'}</MenuItem>
                                    <MenuItem value={true}>{'Yes'}</MenuItem>
                                </Select>
                            </FormControl>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='departments' id='iddepartment' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.departments}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.iddepartment}
                    onRowClick={({ row }) => {
                        const hourly_rate = row?.sedes[0]?.hourly_rate ?? 0;

                        setState(prev => ({ ...prev, department: { ...row, hourly_rate } }));
                    }}
                />
            </div>
        </div>
    );
}

export default Departments;